import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  getDocs,
  writeBatch,
  getDoc,
  setDoc,
  deleteField,
  getCountFromServer,
  limit,
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { InventoryDatabaseItem, InventoryStatus, MetalPrices } from '../types/inventory';
import { inventoryDatabaseBackupService } from './inventoryDatabaseBackupService';

// Collection names
const COLLECTION = 'inventoryDatabase';
const SETTINGS_COLLECTION = 'settings';
const METAL_PRICES_DOC = 'metalPrices';
const METAL_PRICES_LOG_COLLECTION = 'metalPricesLog';

// Metal prices change log entry interface
export interface MetalPriceLogEntry {
  id?: string;
  timestamp: Date;
  userId: string;
  userName: string;
  userEmail: string;
  changes: {
    metal: 'gold' | 'silver' | 'platinum' | 'palladium';
    oldValue: number;
    newValue: number;
  }[];
  previousPrices: {
    gold: number;
    silver: number;
    platinum: number;
    palladium: number;
  };
  newPrices: {
    gold: number;
    silver: number;
    platinum: number;
    palladium: number;
  };
}

// Default metal prices (will be overridden by Firestore)
const DEFAULT_METAL_PRICES: MetalPrices = {
  gold: 2650,      // USD per troy oz
  silver: 31,      // USD per troy oz  
  platinum: 1000,  // USD per troy oz
  palladium: 1000, // USD per troy oz
  updatedAt: new Date()
};

// Cache for metal prices
let cachedMetalPrices: MetalPrices = DEFAULT_METAL_PRICES;

// Helper to convert Firestore timestamp to Date
const toDate = (timestamp: any): Date => {
  if (timestamp?.toDate) return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'string') return new Date(timestamp);
  return new Date();
};

const mapInventoryDocs = (docs: { id: string; data: () => any }[]): InventoryDatabaseItem[] => {
  const items = docs.map((d) => {
    const data = d.data();
    const date = toDate(data.date);
    return {
      id: d.id,
      ...data,
      date,
      age: calculateAge(date),
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
    };
  }) as InventoryDatabaseItem[];
  items.sort((a, b) => b.date.getTime() - a.date.getTime());
  return items;
};

/** Last full snapshot per tab, so switching back does not wait on Firestore again. */
const inventoryByStatus = new Map<InventoryStatus, InventoryDatabaseItem[]>();

export const peekInventoryByStatus = (status: InventoryStatus): InventoryDatabaseItem[] | null =>
  inventoryByStatus.get(status) || null;

/** First page of a tab (uses the existing status+date index). */
export const getInventoryByStatusPreview = async (status: InventoryStatus, n = 50): Promise<InventoryDatabaseItem[]> => {
  const q = query(collection(db, COLLECTION), where('status', '==', status), orderBy('date', 'desc'), limit(n));
  const snap = await getDocs(q);
  return mapInventoryDocs(snap.docs);
};

// ============ METAL PRICES ============

export const getMetalPrices = async (): Promise<MetalPrices> => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, METAL_PRICES_DOC);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      cachedMetalPrices = {
        gold: data.gold || DEFAULT_METAL_PRICES.gold,
        silver: data.silver || DEFAULT_METAL_PRICES.silver,
        platinum: data.platinum || DEFAULT_METAL_PRICES.platinum,
        palladium: data.palladium || DEFAULT_METAL_PRICES.palladium,
        updatedAt: toDate(data.updatedAt)
      };
    }
    return cachedMetalPrices;
  } catch (error) {
    console.error('Error fetching metal prices:', error);
    return cachedMetalPrices;
  }
};

export interface UpdateMetalPricesOptions {
  userId?: string;
  userName?: string;
  userEmail?: string;
}

export const updateMetalPrices = async (
  prices: Partial<MetalPrices>, 
  options?: UpdateMetalPricesOptions
): Promise<void> => {
  // Capture previous prices before update
  const previousPrices = {
    gold: cachedMetalPrices.gold,
    silver: cachedMetalPrices.silver,
    platinum: cachedMetalPrices.platinum,
    palladium: cachedMetalPrices.palladium
  };

  // Calculate changes
  const changes: MetalPriceLogEntry['changes'] = [];
  const metals: ('gold' | 'silver' | 'platinum' | 'palladium')[] = ['gold', 'silver', 'platinum', 'palladium'];
  
  metals.forEach(metal => {
    if (prices[metal] !== undefined && prices[metal] !== previousPrices[metal]) {
      changes.push({
        metal,
        oldValue: previousPrices[metal],
        newValue: prices[metal]!
      });
    }
  });

  // Update metal prices
  const docRef = doc(db, SETTINGS_COLLECTION, METAL_PRICES_DOC);
  const newPrices = {
    gold: prices.gold ?? cachedMetalPrices.gold,
    silver: prices.silver ?? cachedMetalPrices.silver,
    platinum: prices.platinum ?? cachedMetalPrices.platinum,
    palladium: prices.palladium ?? cachedMetalPrices.palladium
  };

  await setDoc(docRef, {
    ...newPrices,
    updatedAt: Timestamp.fromDate(new Date()),
    lastUpdatedBy: options?.userName || 'Unknown',
    lastUpdatedByEmail: options?.userEmail || 'Unknown'
  }, { merge: true });
  
  // Log the change if there were actual changes and user info provided
  if (changes.length > 0) {
    try {
      await addDoc(collection(db, METAL_PRICES_LOG_COLLECTION), {
        timestamp: Timestamp.fromDate(new Date()),
        userId: options?.userId || 'unknown',
        userName: options?.userName || 'Unknown User',
        userEmail: options?.userEmail || 'unknown@unknown.com',
        changes,
        previousPrices,
        newPrices
      });
    } catch (logError) {
      console.error('Error logging metal price change:', logError);
      // Don't throw - logging failure shouldn't prevent price update
    }
  }
  
  // Update cache
  cachedMetalPrices = { ...newPrices, updatedAt: new Date() };
};

// Get metal prices change log
export const getMetalPricesLog = async (limit: number = 50): Promise<MetalPriceLogEntry[]> => {
  try {
    const q = query(
      collection(db, METAL_PRICES_LOG_COLLECTION),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    
    const entries: MetalPriceLogEntry[] = [];
    snapshot.docs.slice(0, limit).forEach(doc => {
      const data = doc.data();
      entries.push({
        id: doc.id,
        timestamp: toDate(data.timestamp),
        userId: data.userId,
        userName: data.userName,
        userEmail: data.userEmail,
        changes: data.changes || [],
        previousPrices: data.previousPrices,
        newPrices: data.newPrices
      });
    });
    
    return entries;
  } catch (error) {
    console.error('Error fetching metal prices log:', error);
    return [];
  }
};

// Subscribe to metal prices log
export const subscribeMetalPricesLog = (
  callback: (entries: MetalPriceLogEntry[]) => void,
  limit: number = 20
) => {
  const q = query(
    collection(db, METAL_PRICES_LOG_COLLECTION),
    orderBy('timestamp', 'desc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const entries: MetalPriceLogEntry[] = [];
    snapshot.docs.slice(0, limit).forEach(doc => {
      const data = doc.data();
      entries.push({
        id: doc.id,
        timestamp: toDate(data.timestamp),
        userId: data.userId,
        userName: data.userName,
        userEmail: data.userEmail,
        changes: data.changes || [],
        previousPrices: data.previousPrices,
        newPrices: data.newPrices
      });
    });
    callback(entries);
  });
};

export const subscribeMetalPrices = (callback: (prices: MetalPrices) => void) => {
  const docRef = doc(db, SETTINGS_COLLECTION, METAL_PRICES_DOC);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      cachedMetalPrices = {
        gold: data.gold || DEFAULT_METAL_PRICES.gold,
        silver: data.silver || DEFAULT_METAL_PRICES.silver,
        platinum: data.platinum || DEFAULT_METAL_PRICES.platinum,
        palladium: data.palladium || DEFAULT_METAL_PRICES.palladium,
        updatedAt: toDate(data.updatedAt)
      };
      callback(cachedMetalPrices);
    } else {
      callback(DEFAULT_METAL_PRICES);
    }
  });
};

// ============ BARCODE CALCULATION ============
// Converts SKU to barcode using prefix mapping
const SKU_PREFIX_MAP: Record<string, string> = {
  'PM100': '200000',
  'PM200': '200100',
  'PM201': '200101',
  'PM202': '200102',
  'PM300': '200200',
  'PM400': '200300',
  'PM401': '200301',
  'PM600': '200500'
};

// Pasted SKUs often carry leading/trailing spaces, NBSPs, or zero-width chars,
// which break barcode generation and exact-match lookups (transfers, scans).
export const normalizeSku = (sku: string): string =>
  (sku || '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .trim()
    .replace(/^pm/i, 'PM');

export const calculateBarcode = (rawSku: string): string => {
  const sku = normalizeSku(rawSku);
  if (!sku || !sku.startsWith('PM')) return '';
  const prefix = sku.substring(0, 5);
  const barcodePrefix = SKU_PREFIX_MAP[prefix];
  if (!barcodePrefix) return '';
  return barcodePrefix + sku.substring(5);
};

// ============ AGE CALCULATION ============
export const calculateAge = (date: Date): number => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return 0;
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  const age = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  // Return 0 if negative (date is in future - likely missing year in import)
  return age < 0 ? 0 : age;
};

// ============ NEW MELT CALCULATION ============
// Matches Google Sheets formula: applies silver price per gram to ALL tiers
export const calculateNewMelt = (
  metal: string, 
  weightGrams: number, 
  prices: MetalPrices = cachedMetalPrices
): number => {
  if (!metal || !weightGrams || weightGrams <= 0) return 0;
  
  const g = weightGrams;
  const OZ_TO_G = 31.1035;
  const goldPerGram = prices.gold / OZ_TO_G;
  const silverPerGram = prices.silver / OZ_TO_G;
  const platinumPerGram = prices.platinum / OZ_TO_G;
  const palladiumPerGram = prices.palladium / OZ_TO_G;
  
  // Tier multiplier for silver/sterling
  const tierMult = (grams: number): number => {
    if (grams < 26) return 4;
    if (grams < 51) return 3;
    if (grams < 101) return 2.5;
    if (grams < 201) return 1.75;
    if (grams < 501) return 1.25;
    return 1;
  };
  
  // Debug logging
  console.log(`[calculateNewMelt] Metal: ${metal}, Weight: ${g}g, Silver/oz: ${prices.silver}, Silver/g: ${silverPerGram.toFixed(4)}`);
  
  switch (metal) {
    case 'Silver 999':
      // All tiers: grams * tierMultiplier * silverPerGram
      const silver999Result = g * tierMult(g) * silverPerGram;
      console.log(`[calculateNewMelt] Silver 999: ${g} * ${tierMult(g)} * ${silverPerGram.toFixed(4)} = $${silver999Result.toFixed(2)}`);
      return silver999Result;
      
    case 'Sterling':
      // All tiers: grams * 0.925 * tierMultiplier * silverPerGram
      const sterlingResult = g * 0.925 * tierMult(g) * silverPerGram;
      console.log(`[calculateNewMelt] Sterling: ${g} * 0.925 * ${tierMult(g)} * ${silverPerGram.toFixed(4)} = $${sterlingResult.toFixed(2)}`);
      return sterlingResult;
      
    case 'Gold Filled':
      return g * 3;
      
    case 'Gold 10K':
      return g * (10/24) * goldPerGram;
      
    case 'Gold 14K':
      return g * (14/24) * goldPerGram;
      
    case 'Gold 18K':
      return g * (18/24) * goldPerGram;
      
    case 'Gold 20K':
      return g * (20/24) * goldPerGram;
      
    case 'Gold 22K':
      return g * (22/24) * goldPerGram;
      
    case 'Gold 24K':
      return g * goldPerGram;
      
    case 'Platinum':
      return g * platinumPerGram;
      
    case 'Palladium':
      return g * palladiumPerGram;
    
    case 'Coin':
      return (g * silverPerGram) + 25;
      
    case 'Watch':
    case 'Other':
    default:
      return 0;
  }
};

// ============ WHOLESALE (WS) CALCULATION ============
export const calculateWholesale = (
  cost: number,
  newMelt: number,
  stones: number,
  dpremWws: number
): number => {
  const calculatedValue = newMelt + stones + dpremWws;
  return Math.max(cost, calculatedValue);
};

// ============ MARGIN CALCULATION ============
export const calculateMargin = (price: number, wholesale: number): number => {
  if (!price || price <= 0) return 0;
  return ((price - wholesale) / price) * 100;
};

// ============ DAY 0 PRICE CALCULATION ============
export const calculateDay0 = (
  metal: string,
  weightGrams: number,
  wholesale: number,
  itemType: string
): number => {
  if (!wholesale || wholesale <= 0) return 0;
  
  const ws = wholesale;
  const g = weightGrams;
  
  // PANDORA items
  if (itemType === 'PANDORA') {
    return Math.ceil(ws / 10) * 10;
  }
  
  // Gold items - use weight-based multipliers
  if (metal.startsWith('Gold')) {
    let multiplier: number;
    if (g < 5) multiplier = 1.6;
    else if (g < 11) multiplier = 1.5;
    else if (g < 31) multiplier = 1.4;
    else multiplier = 1.35;
    
    return Math.ceil((ws * multiplier) / 10) * 10;
  }
  
  // High value items ($5000+)
  if (ws >= 5000) {
    let multiplier: number;
    if (ws < 20000) multiplier = 1.377;
    else if (ws < 50000) multiplier = 1.357;
    else multiplier = 1.327;
    
    return Math.ceil((ws * multiplier) / 10) * 10;
  }
  
  // Medium value items ($1000-$5000)
  if (ws >= 1000) {
    return Math.ceil((ws * 1.527) / 10) * 10;
  }
  
  // Low value items (<$1000)
  let value: number;
  if (ws <= 50) {
    value = ws * 1.627 + 25;
  } else if (ws < 100) {
    value = ws * 1.627 + 20;
  } else {
    value = ws * 1.627;
  }
  
  return Math.ceil(value / 10) * 10;
};

// ============ EBAY0 PRICE CALCULATION ============
export const calculateEbay0 = (wholesale: number, day0: number, metal: string = ''): number => {
  if (!wholesale || wholesale <= 0) return 0;
  
  const ws = wholesale;
  let value: number;
  
  // Watch-specific tiered calculation (based on price/day0, NOT wholesale)
  if (metal === 'Watch') {
    const price = day0; // day0 = price column for watches
    if (!price || price <= 0) return 0;
    if (price < 1000) value = price * 1.15;
    else if (price < 2500) value = price * 1.12;
    else if (price < 5000) value = price * 1.11;
    else if (price < 7500) value = price * 1.09;
    else if (price < 10000) value = price * 1.085;
    else if (price < 15000) value = price * 1.07;
    else value = price * 1.06;
    return Math.ceil(value / 10) * 10;
  }
  
  // Non-watch items: tiered calculation based on day0
  if (ws >= 7500) {
    value = day0 + ((day0 - 7500) * 0.0235) + 546;
  } else if (ws >= 1000) {
    value = day0 + ((day0 - 1000) * 0.065) + 123.5;
  } else {
    value = day0 * 1.1235;
  }
  
  return Math.ceil(value / 10) * 10;
};

// ============ DENY PRICE CALCULATION ============
export const calculateDeny = (wholesale: number): number => {
  if (!wholesale || wholesale <= 0) return 0;
  
  const ws = wholesale;
  
  if (ws >= 5000) {
    return ws;
  }
  
  if (ws >= 1000) {
    return ws * 1.175;
  }
  
  // < $1000
  if (ws <= 50) {
    return (ws * 1.227) + 12;
  }
  if (ws < 100) {
    return (ws * 1.227) + 7;
  }
  return ws * 1.227;
};

// ============ CALCULATE ALL DERIVED FIELDS ============
// Optional custom formula support - formulas are stored in Firestore
// and can be edited via the Formula Editor in the Inventory Database page
import { getInventoryFormulas, evaluateFormula, InventoryFormulas, DEFAULT_FORMULAS } from './inventoryFormulaService';

// Cache for custom formulas
let cachedFormulas: InventoryFormulas | null = null;
let formulasCacheTime: number = 0;
const FORMULA_CACHE_TTL = 60000; // 1 minute cache

const getFormulasWithCache = async (): Promise<InventoryFormulas> => {
  const now = Date.now();
  if (cachedFormulas && (now - formulasCacheTime) < FORMULA_CACHE_TTL) {
    return cachedFormulas;
  }
  
  try {
    cachedFormulas = await getInventoryFormulas();
    formulasCacheTime = now;
    return cachedFormulas;
  } catch (error) {
    console.error('Error loading custom formulas, using defaults:', error);
    return DEFAULT_FORMULAS;
  }
};

// Clear formula cache (call after saving formulas)
export const clearFormulaCache = () => {
  cachedFormulas = null;
  formulasCacheTime = 0;
};

export const calculateAllFields = async (
  item: Partial<InventoryDatabaseItem>,
  prices?: MetalPrices,
  useCustomFormulas: boolean = true
): Promise<Partial<InventoryDatabaseItem>> => {
  const metalPrices = prices || await getMetalPrices();
  
  // Get custom formulas if enabled
  const formulas = useCustomFormulas ? await getFormulasWithCache() : DEFAULT_FORMULAS;
  
  const date = item.date instanceof Date ? item.date : new Date(item.date || new Date());
  const today = new Date();
  
  // Calculate age
  const age = calculateAge(date);
  
  // Calculate barcode from SKU if not provided
  const sku = item.sku !== undefined ? normalizeSku(item.sku) : undefined;
  const barcode = (item.barcode || '').trim() || calculateBarcode(sku || '');
  
  // Calculate melt value - ALWAYS recalculate based on metal, weight, and prices
  // (previously was keeping old saved values which caused stale calculations)
  let newMelt: number;
  
  // ALWAYS use the calculateNewMelt function for consistency with Google Sheets formula
  // Custom formulas are disabled for newMelt to ensure correct tiered calculations
  newMelt = calculateNewMelt(item.metal || '', item.weightGrams || 0, metalPrices);
  
  // Calculate dpremWws (Designer Premium) based on dpremType
  let dpremWws: number;
  const dpremType = item.dpremType || 'manual';
  const multiplier = item.dpremMultiplier || 0;
  
  switch (dpremType) {
    case 'retail_pct':
      // Formula: retail × percentage (e.g., 0.25 = 25%)
      dpremWws = (item.retail || 0) * multiplier;
      break;
      
    case 'retail_pct_minus_melt':
      // Formula: (retail × percentage) - newMelt
      dpremWws = ((item.retail || 0) * multiplier) - newMelt;
      break;
      
    case 'melt_pct':
      // Formula: melt × percentage (e.g., 0.5 = 50% of melt)
      dpremWws = newMelt * multiplier;
      break;
      
    case 'fixed_minus_melt':
      // Formula: fixedValue - newMelt (e.g., 60 - melt)
      dpremWws = multiplier - newMelt;
      break;
      
    case 'weight_mult_minus_melt':
      // Formula: (weightGrams × multiplier) - newMelt (e.g., grams × 1.5 - melt)
      dpremWws = ((item.weightGrams || 0) * multiplier) - newMelt;
      break;
      
    case 'manual':
    default:
      // Check global formula first, then manual value
      if (formulas.dpremWws?.enabled && formulas.dpremWws.formula && formulas.dpremWws.formula !== '0') {
        try {
          const result = evaluateFormula(formulas.dpremWws.formula, {
            retail: item.retail || 0,
            newMelt,
            cost: item.cost || 0,
            stones: item.stones || 0,
            weightGrams: item.weightGrams || 0
          });
          dpremWws = typeof result === 'number' ? result : 0;
        } catch {
          dpremWws = item.dpremWws || 0;
        }
      } else {
        dpremWws = item.dpremWws || 0;
      }
      break;
  }
  
  // Calculate wholesale
  let wholesale: number;
  if (formulas.wholesale?.enabled && formulas.wholesale.formula !== DEFAULT_FORMULAS.wholesale.formula) {
    try {
      const result = evaluateFormula(formulas.wholesale.formula, {
        cost: item.cost || 0,
        newMelt,
        stones: item.stones || 0,
        dpremWws
      });
      wholesale = typeof result === 'number' ? result : 0;
    } catch {
      wholesale = calculateWholesale(item.cost || 0, newMelt, item.stones || 0, dpremWws);
    }
  } else {
    wholesale = calculateWholesale(item.cost || 0, newMelt, item.stones || 0, dpremWws);
  }
  
  // Calculate day0 — Price always overrides Day0 when set
  let day0: number;
  if (item.price && item.price > 0) {
    day0 = item.price;
  } else {
    day0 = item.metal === 'Watch'
      ? 0
      : calculateDay0(item.metal || '', item.weightGrams || 0, wholesale, item.type || '');
  }
  
  // Calculate ebay0 - ALWAYS use the hardcoded function for reliability
  // For watches: tiered markup based on wholesale
  // For non-watches: tiered calculation based on day0
  const ebay0 = calculateEbay0(wholesale, day0, item.metal || '');
  
  // Calculate deny
  let deny: number;
  if (formulas.deny?.enabled && formulas.deny.formula !== DEFAULT_FORMULAS.deny.formula) {
    try {
      const result = evaluateFormula(formulas.deny.formula, {
        wholesale
      });
      deny = typeof result === 'number' ? result : 0;
    } catch {
      deny = calculateDeny(wholesale);
    }
  } else {
    deny = calculateDeny(wholesale);
  }
  
  // Calculate margin using price (user-entered) and wholesale
  const price = item.price || day0; // Default price to day0 if not set
  let margin: number;
  if (formulas.margin?.enabled && formulas.margin.formula !== DEFAULT_FORMULAS.margin.formula) {
    try {
      const result = evaluateFormula(formulas.margin.formula, {
        price,
        wholesale
      });
      margin = typeof result === 'number' ? result : 0;
    } catch {
      margin = calculateMargin(price, wholesale);
    }
  } else {
    margin = calculateMargin(price, wholesale);
  }
  
  return {
    ...item,
    ...(sku !== undefined ? { sku } : {}),
    barcode,
    age,
    newMelt: Math.round(newMelt * 100) / 100,
    dpremWws: Math.round(dpremWws * 100) / 100,
    wholesale: Math.round(wholesale * 100) / 100,
    day0: Math.round(day0 * 100) / 100,
    ebay0: Math.round(ebay0 * 100) / 100,
    deny: Math.round(deny * 100) / 100,
    margin: Math.round(margin * 100) / 100,
    price: Math.round(price * 100) / 100
  };
};

// ============ SUBSCRIPTIONS ============

// Subscribe to all inventory items
export const subscribeInventory = (
  callback: (items: InventoryDatabaseItem[]) => void
) => {
  const inventoryRef = collection(db, COLLECTION);
  const q = query(inventoryRef, orderBy('date', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map((doc) => {
      const data = doc.data();
      const date = toDate(data.date);
      return {
        id: doc.id,
        ...data,
        date,
        age: calculateAge(date),
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt)
      };
    }) as InventoryDatabaseItem[];
    callback(items);
  });
};

// Subscribe to inventory by status (for tabs)
// 'jewelry' is a real status value for jewelry items
export const subscribeInventoryByStatus = (
  status: InventoryStatus,
  callback: (items: InventoryDatabaseItem[]) => void
) => {
  const inventoryRef = collection(db, COLLECTION);
  
  console.log(`[InventoryService] Subscribing to status: ${status}`);
  
  // Query by status only, sort client-side to avoid index requirement
  const q = query(inventoryRef, where('status', '==', status));

  return onSnapshot(q, (snapshot) => {
    console.log(`[InventoryService] Got ${snapshot.docs.length} items for status: ${status}`);
    const items = mapInventoryDocs(snapshot.docs);
    inventoryByStatus.set(status, items);
    callback(items);
  }, (error) => {
    console.error('[InventoryService] Error subscribing to inventory:', error);
    console.error('[InventoryService] Error details:', error.message);
    callback([]);
  });
};

// Subscribe to inventory items that have a disposition set (for Decisions tab)
export const subscribeInventoryByDisposition = (
  callback: (items: InventoryDatabaseItem[]) => void
) => {
  const inventoryRef = collection(db, COLLECTION);
  const q = query(inventoryRef, where('disposition', 'in', ['melt', 'wholesale', 'keep', 'reprice', 'needs_correcting']));

  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map((doc) => {
      const data = doc.data();
      const date = toDate(data.date);
      return {
        id: doc.id,
        ...data,
        date,
        age: calculateAge(date),
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        dispositionSetAt: data.dispositionSetAt ? toDate(data.dispositionSetAt) : undefined,
      };
    }) as InventoryDatabaseItem[];

    items.sort((a, b) => {
      const aTime = a.dispositionSetAt?.getTime() || 0;
      const bTime = b.dispositionSetAt?.getTime() || 0;
      return bTime - aTime;
    });

    callback(items);
  }, (error) => {
    console.error('[InventoryService] Error subscribing to dispositions:', error);
    callback([]);
  });
};

export const clearInventoryDisposition = async (id: string): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  await updateDoc(docRef, {
    disposition: deleteField(),
    dispositionSetAt: deleteField(),
    dispositionSetBy: deleteField(),
  });
};

// ============ CRUD OPERATIONS ============

// Firestore rejects `undefined` field values. Items that aren't full jewelry
// records (e.g. watches) legitimately leave fields like `dpremMultiplier`
// unset, and `calculateAllFields` spreads those undefineds straight through.
// Strip them so writes don't fail with "Unsupported field value: undefined".
const stripUndefined = <T extends Record<string, any>>(obj: T): T => {
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) cleaned[key] = value;
  }
  return cleaned as T;
};

export const addInventoryDatabaseItem = async (item: Omit<InventoryDatabaseItem, 'id'>): Promise<string> => {
  const calculatedItem = await calculateAllFields(item);
  const now = new Date();
  const docRef = await addDoc(collection(db, COLLECTION), stripUndefined({
    ...calculatedItem,
    date: Timestamp.fromDate(item.date instanceof Date ? item.date : new Date(item.date || new Date())),
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now)
  }));
  
  // Sync to backup sheet (async, non-blocking)
  inventoryDatabaseBackupService.queueOperation(async () => {
    await inventoryDatabaseBackupService.addItem({
      ...calculatedItem,
      id: docRef.id,
      date: item.date instanceof Date ? item.date : new Date(item.date || new Date()),
      createdAt: now,
      updatedAt: now
    } as InventoryDatabaseItem);
  });
  
  return docRef.id;
};

export const updateInventoryDatabaseItem = async (id: string, updates: Partial<InventoryDatabaseItem>): Promise<void> => {
  // Get current item to merge with updates for recalculation
  const docRef = doc(db, COLLECTION, id);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }
  
  const currentData = docSnap.data();
  const previousStatus = currentData.status as InventoryStatus;
  const mergedItem = {
    ...currentData,
    ...updates,
    date: updates.date 
      ? (updates.date instanceof Date ? updates.date : new Date(updates.date))
      : toDate(currentData.date)
  };
  
  // Recalculate all derived fields
  const calculatedItem = await calculateAllFields(mergedItem);
  const now = new Date();
  
  const updateData: any = {
    ...calculatedItem,
    updatedAt: Timestamp.fromDate(now)
  };
  
  if (calculatedItem.date) {
    updateData.date = Timestamp.fromDate(
      calculatedItem.date instanceof Date ? calculatedItem.date : new Date(calculatedItem.date)
    );
  }
  
  await updateDoc(docRef, stripUndefined(updateData));
  
  // Sync to backup sheet (async, non-blocking)
  inventoryDatabaseBackupService.queueOperation(async () => {
    await inventoryDatabaseBackupService.updateItem({
      ...calculatedItem,
      id,
      date: calculatedItem.date instanceof Date ? calculatedItem.date : new Date(calculatedItem.date),
      createdAt: toDate(currentData.createdAt),
      updatedAt: now
    } as InventoryDatabaseItem, previousStatus);
  });
};

export const deleteInventoryDatabaseItem = async (id: string): Promise<void> => {
  // Get item status before deleting so we know which tabs to remove from
  const docRef = doc(db, COLLECTION, id);
  const docSnap = await getDoc(docRef);
  
  let status: InventoryStatus = 'draft'; // Default
  if (docSnap.exists()) {
    status = (docSnap.data().status as InventoryStatus) || 'draft';
  }
  
  await deleteDoc(docRef);
  
  // Sync to backup sheet (async, non-blocking)
  inventoryDatabaseBackupService.queueOperation(async () => {
    await inventoryDatabaseBackupService.deleteItem(id, status);
  });
};

// Update status (for moving between tabs)
export const updateInventoryStatus = async (id: string, status: InventoryStatus, changedByUid?: string, changedByName?: string): Promise<void> => {
  // Get current item to track status change for backup sync
  const docRef = doc(db, COLLECTION, id);
  const docSnap = await getDoc(docRef);
  
  let previousStatus: InventoryStatus = 'draft';
  let currentData: any = {};
  if (docSnap.exists()) {
    currentData = docSnap.data();
    previousStatus = (currentData.status as InventoryStatus) || 'draft';
  }
  
  const now = new Date();
  await updateDoc(docRef, {
    status,
    updatedAt: Timestamp.fromDate(now)
  });
  
  // Log to sku_history (LFAC — non-blocking)
  if (previousStatus !== status && currentData.sku) {
    import('./inventoryEventService').then(({ inventoryEventService }) => {
      inventoryEventService.logStatusChange({
        sku: currentData.sku,
        barcode: currentData.barcode,
        inventoryItemId: id,
        previousStatus,
        newStatus: status,
        statusField: 'status',
        userId: changedByUid || 'system',
        userName: changedByName,
        source: 'manual',
      });
    }).catch(() => {});
  }

  // Sync to backup sheet (async, non-blocking)
  if (docSnap.exists()) {
    inventoryDatabaseBackupService.queueOperation(async () => {
      await inventoryDatabaseBackupService.updateItem({
        ...currentData,
        id,
        status,
        date: toDate(currentData.date),
        createdAt: toDate(currentData.createdAt),
        updatedAt: now
      } as InventoryDatabaseItem, previousStatus);
    });
  }
};

// ============ SEARCH ============

export const searchInventoryBySku = async (sku: string): Promise<InventoryDatabaseItem[]> => {
  const inventoryRef = collection(db, COLLECTION);
  const q = query(inventoryRef, where('sku', '==', sku));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    const date = toDate(data.date);
    return {
      id: doc.id,
      ...data,
      date,
      age: calculateAge(date),
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt)
    };
  }) as InventoryDatabaseItem[];
};

export const searchInventoryByBarcode = async (barcode: string): Promise<InventoryDatabaseItem[]> => {
  const inventoryRef = collection(db, COLLECTION);
  const q = query(inventoryRef, where('barcode', '==', barcode));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    const date = toDate(data.date);
    return {
      id: doc.id,
      ...data,
      date,
      age: calculateAge(date),
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt)
    };
  }) as InventoryDatabaseItem[];
};

// ============ BULK OPERATIONS ============

export const bulkImportInventory = async (items: Omit<InventoryDatabaseItem, 'id'>[]): Promise<number> => {
  const batch = writeBatch(db);
  let count = 0;

  for (const item of items) {
    const calculatedItem = calculateInventoryFields(item);
    const docRef = doc(collection(db, COLLECTION));
    batch.set(docRef, {
      ...calculatedItem,
      date: Timestamp.fromDate(item.date instanceof Date ? item.date : new Date(item.date)),
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date())
    });
    count++;

    // Firestore batch limit is 500
    if (count % 500 === 0) {
      await batch.commit();
    }
  }

  if (count % 500 !== 0) {
    await batch.commit();
  }

  return count;
};

// Get all inventory items (for export/stats) - one-time fetch
export const getAllInventory = async (): Promise<InventoryDatabaseItem[]> => {
  const inventoryRef = collection(db, COLLECTION);
  const snapshot = await getDocs(inventoryRef);
  
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    const date = toDate(data.date);
    return {
      id: doc.id,
      ...data,
      date,
      age: calculateAge(date),
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt)
    };
  }) as InventoryDatabaseItem[];
};

const ALL_INVENTORY_KEEP_MS = 15 * 60_000;
const allInventoryFeed = {
  unsub: null as null | (() => void),
  items: null as InventoryDatabaseItem[] | null,
  byId: new Map<string, InventoryDatabaseItem>(),
  listeners: new Set<(items: InventoryDatabaseItem[]) => void>(),
  stopTimer: undefined as ReturnType<typeof setTimeout> | undefined,
};

/**
 * ALL inventory items, live (cross-tab search). One listener is shared and kept for a while after
 * the last screen stops using it, so clearing the search box and searching again is instant.
 */
export const subscribeToAllInventory = (
  callback: (items: InventoryDatabaseItem[]) => void
) => {
  const feed = allInventoryFeed;
  feed.listeners.add(callback);
  clearTimeout(feed.stopTimer);
  if (feed.items) {
    const current = feed.items;
    queueMicrotask(() => feed.listeners.has(callback) && callback(current));
  }

  if (!feed.unsub) {
    feed.unsub = onSnapshot(collection(db, COLLECTION), (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') feed.byId.delete(change.doc.id);
        else feed.byId.set(change.doc.id, mapInventoryDocs([change.doc])[0]);
      });
      const items = Array.from(feed.byId.values());
      items.sort((a, b) => b.date.getTime() - a.date.getTime());
      feed.items = items;
      feed.listeners.forEach((l) => l(items));
    }, (error) => {
      console.error('[InventoryService] Error subscribing to all inventory:', error);
      feed.unsub = null;
      feed.listeners.forEach((l) => l([]));
    });
  }

  return () => {
    feed.listeners.delete(callback);
    if (feed.listeners.size > 0) return;
    clearTimeout(feed.stopTimer);
    feed.stopTimer = setTimeout(() => {
      if (feed.listeners.size > 0) return;
      feed.unsub?.();
      feed.unsub = null;
      feed.items = null;
      feed.byId.clear();
    }, ALL_INVENTORY_KEEP_MS);
  };
};

// Get all existing SKUs (for duplicate checking during import)
export const getExistingSkus = async (): Promise<Set<string>> => {
  const inventoryRef = collection(db, COLLECTION);
  const snapshot = await getDocs(inventoryRef);
  const skus = new Set<string>();
  snapshot.docs.forEach(doc => {
    const sku = doc.data().sku;
    if (sku) {
      skus.add(sku.toUpperCase().trim());
    }
  });
  console.log(`[InventoryService] Found ${skus.size} existing SKUs in database`);
  return skus;
};

// Get inventory counts by status (aggregation queries — does not download documents)
export const getInventoryCounts = async (): Promise<Record<InventoryStatus, number>> => {
  const inventoryRef = collection(db, COLLECTION);
  const [draft, jewelry, watch, sold, consignment] = await Promise.all([
    getCountFromServer(query(inventoryRef, where('status', '==', 'draft'))),
    getCountFromServer(query(inventoryRef, where('status', '==', 'jewelry'))),
    getCountFromServer(query(inventoryRef, where('status', '==', 'watch'))),
    getCountFromServer(query(inventoryRef, where('status', '==', 'sold'))),
    getCountFromServer(query(inventoryRef, where('status', '==', 'consignment'))),
  ]);

  // Ignored items stay in the collection but are excluded from every tab count.
  const ignoredByStatus: Record<string, number> = {};
  const ignoredSnap = await getDocs(query(inventoryRef, where('ignored', '==', true)));
  ignoredSnap.docs.forEach((d) => {
    const s = String(d.data().status || '');
    ignoredByStatus[s] = (ignoredByStatus[s] || 0) + 1;
  });
  const net = (s: InventoryStatus, total: number) => Math.max(0, total - (ignoredByStatus[s] || 0));

  return {
    draft: net('draft', draft.data().count),
    jewelry: net('jewelry', jewelry.data().count),
    watch: net('watch', watch.data().count),
    sold: net('sold', sold.data().count),
    consignment: net('consignment', consignment.data().count),
  };
};

// Debug function to check database contents
export const debugInventoryDatabase = async (): Promise<void> => {
  try {
    const inventoryRef = collection(db, COLLECTION);
    const snapshot = await getDocs(inventoryRef);
    
    console.log('[InventoryService] === DEBUG: Database Contents ===');
    console.log('[InventoryService] Total documents:', snapshot.docs.length);
    
    const statusCounts: Record<string, number> = {};
    snapshot.docs.forEach(doc => {
      const status = doc.data().status || 'undefined';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    
    console.log('[InventoryService] Status breakdown:', statusCounts);
    
    // Show first 3 items for reference
    snapshot.docs.slice(0, 3).forEach((doc, idx) => {
      const data = doc.data();
      console.log(`[InventoryService] Item ${idx + 1}:`, {
        id: doc.id,
        sku: data.sku,
        status: data.status,
        date: data.date
      });
    });
  } catch (error) {
    console.error('[InventoryService] Debug error:', error);
  }
};

// Migrate old 'active' or 'all' status to 'jewelry' (run once if needed)
export const migrateToJewelry = async (): Promise<number> => {
  const inventoryRef = collection(db, COLLECTION);
  
  // Migrate 'active' status
  const qActive = query(inventoryRef, where('status', '==', 'active'));
  const snapshotActive = await getDocs(qActive);
  
  // Migrate 'all' status
  const qAll = query(inventoryRef, where('status', '==', 'all'));
  const snapshotAll = await getDocs(qAll);
  
  const totalDocs = snapshotActive.docs.length + snapshotAll.docs.length;
  
  if (totalDocs === 0) {
    console.log('[InventoryService] No items with "active" or "all" status to migrate');
    return 0;
  }
  
  console.log(`[InventoryService] Migrating ${totalDocs} items to "jewelry" status`);
  
  const batch = writeBatch(db);
  snapshotActive.docs.forEach(doc => {
    batch.update(doc.ref, { status: 'jewelry' });
  });
  snapshotAll.docs.forEach(doc => {
    batch.update(doc.ref, { status: 'jewelry' });
  });
  
  await batch.commit();
  console.log('[InventoryService] Migration complete');
  return totalDocs;
};

// ============ BACKUP SYNC ============

/**
 * Perform a full sync of all inventory items to the backup Google Sheet
 * This replaces all data in the backup sheet with current Firebase data
 */
export const fullSyncToBackupSheet = async (): Promise<{ 
  success: boolean; 
  counts: Record<string, number>;
  error?: string;
}> => {
  try {
    console.log('[InventoryService] Starting full backup sync...');
    const items = await getAllInventory();
    console.log(`[InventoryService] Syncing ${items.length} items to backup sheet`);
    
    const result = await inventoryDatabaseBackupService.fullSync(items);
    
    if (result.success) {
      console.log('[InventoryService] Full backup sync completed:', result.counts);
    } else {
      console.error('[InventoryService] Full backup sync failed');
    }
    
    return result;
  } catch (error: any) {
    console.error('[InventoryService] Full backup sync error:', error);
    return { 
      success: false, 
      counts: {},
      error: error?.message || 'Unknown error'
    };
  }
};

/**
 * Enable or disable backup sync
 */
export const setBackupSyncEnabled = (enabled: boolean): void => {
  inventoryDatabaseBackupService.setEnabled(enabled);
};

// ============ RECALCULATE ALL ITEMS ============

/**
 * Recalculate all inventory items with current metal prices
 * This updates newMelt, wholesale, margin, day0, ebay0, deny for all items
 */
export const recalculateAllItems = async (): Promise<{
  success: boolean;
  count: number;
  error?: string;
}> => {
  try {
    console.log('[InventoryService] Starting recalculation of all items...');
    
    // Get fresh metal prices
    const metalPrices = await getMetalPrices();
    console.log('[InventoryService] Using metal prices:', metalPrices);
    
    // Get all items
    const items = await getAllInventory();
    console.log(`[InventoryService] Recalculating ${items.length} items`);
    
    // Process in batches of 500 (Firestore limit)
    const BATCH_SIZE = 500;
    let processedCount = 0;
    
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const batchItems = items.slice(i, i + BATCH_SIZE);
      
      for (const item of batchItems) {
        // Force recalculation by clearing the existing newMelt value
        // This ensures calculateAllFields doesn't skip the calculation
        const itemForRecalc = {
          ...item,
          newMelt: 0  // Clear so it recalculates
        };
        
        // Recalculate fields with fresh metal prices
        const recalculated = await calculateAllFields(itemForRecalc, metalPrices, true);
        
        // Update in Firestore
        const docRef = doc(db, COLLECTION, item.id);
        batch.update(docRef, {
          newMelt: recalculated.newMelt,
          dpremWws: recalculated.dpremWws,
          wholesale: recalculated.wholesale,
          margin: recalculated.margin,
          day0: recalculated.day0,
          ebay0: recalculated.ebay0,
          deny: recalculated.deny,
          age: recalculated.age,
          updatedAt: Timestamp.fromDate(new Date())
        });
        processedCount++;
      }
      
      await batch.commit();
      console.log(`[InventoryService] Processed batch ${Math.floor(i / BATCH_SIZE) + 1}, total: ${processedCount}`);
    }
    
    console.log(`[InventoryService] Recalculation complete: ${processedCount} items updated`);
    return { success: true, count: processedCount };
  } catch (error: any) {
    console.error('[InventoryService] Recalculation error:', error);
    return { 
      success: false, 
      count: 0, 
      error: error?.message || 'Unknown error' 
    };
  }
};

// ============ BULK UPDATE DPREM SETTINGS ============

/**
 * Bulk update DPREM settings for items by SKU
 * @param updates Array of { sku, dpremType?, dpremPercent?, dpremWws? }
 * @param recalculate Whether to recalculate all fields after updating
 */
export const bulkUpdateDprem = async (
  updates: { 
    sku: string; 
    dpremType?: 'manual' | 'retail_pct' | 'retail_pct_minus_melt' | 'melt_pct' | 'fixed_minus_melt' | 'weight_mult_minus_melt';
    dpremMultiplier?: number;
    dpremWws?: number;
  }[],
  recalculate: boolean = true
): Promise<{
  success: boolean;
  updated: number;
  notFound: string[];
  error?: string;
}> => {
  try {
    console.log(`[InventoryService] Bulk updating DPREM for ${updates.length} items...`);
    
    // Get all items to find matches
    const allItems = await getAllInventory();
    const itemMap = new Map(allItems.map(item => [item.sku.toUpperCase(), item]));
    
    const notFound: string[] = [];
    let updatedCount = 0;
    
    // Process in batches
    const BATCH_SIZE = 500;
    const validUpdates = updates.filter(u => {
      const item = itemMap.get(u.sku.toUpperCase());
      if (!item) {
        notFound.push(u.sku);
        return false;
      }
      return true;
    });
    
    for (let i = 0; i < validUpdates.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const batchUpdates = validUpdates.slice(i, i + BATCH_SIZE);
      
      for (const update of batchUpdates) {
        const item = itemMap.get(update.sku.toUpperCase());
        if (item) {
          const docRef = doc(db, COLLECTION, item.id);
          const updateData: Record<string, any> = {
            updatedAt: Timestamp.fromDate(new Date())
          };
          
          if (update.dpremType !== undefined) {
            updateData.dpremType = update.dpremType;
          }
          if (update.dpremMultiplier !== undefined) {
            updateData.dpremMultiplier = update.dpremMultiplier;
          }
          if (update.dpremWws !== undefined) {
            updateData.dpremWws = update.dpremWws;
          }
          
          batch.update(docRef, updateData);
          updatedCount++;
        }
      }
      
      await batch.commit();
    }
    
    console.log(`[InventoryService] Updated ${updatedCount} items, ${notFound.length} not found`);
    
    // Recalculate if requested
    if (recalculate && updatedCount > 0) {
      console.log('[InventoryService] Triggering recalculation...');
      await recalculateAllItems();
    }
    
    return { success: true, updated: updatedCount, notFound };
  } catch (error: any) {
    console.error('[InventoryService] Bulk update error:', error);
    return { 
      success: false, 
      updated: 0, 
      notFound: [],
      error: error?.message || 'Unknown error' 
    };
  }
};
